import React, { useState } from 'react';
import {
  Car,
  ClipboardList,
  Timer,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Package,
  Clock,
  PhoneCall,
  Shield,
  Scale,
  Zap
} from 'lucide-react';
import GoogleReviews from '../components/GoogleReviews';

interface ServiceCardProps {
  image: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function ServiceCard({ image, title, description, icon }: ServiceCardProps) {
  return (
    <div className="card overflow-hidden">
      <div className="relative">
        <img 
          src={image} 
          alt={title}
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="flex items-center space-x-2">
            {icon}
            <h3 className="text-xl font-semibold">{title}</h3>
          </div>
        </div>
      </div>
      <div className="p-6 bg-white">
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function ContactCard({ icon, title, content }: { icon: React.ReactNode; title: string; content: string }) {
  return (
    <div className="flex items-start space-x-4">
      <div className="text-[#FF6B35]">{icon}</div>
      <div>
        <h3 className="font-semibold text-[#1B4965]">{title}</h3>
        <p className="text-gray-600">{content}</p>
      </div>
    </div>
  );
}

function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: ''
  });

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name ist erforderlich';
    if (!formData.email.trim()) return 'E-Mail ist erforderlich';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Ungültige E-Mail-Adresse';
    if (!formData.message.trim()) return 'Nachricht ist erforderlich';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: error
      });
      return;
    }

    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: ''
    });

    try {
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message: 'Vielen Dank für Ihre Nachricht. Wir werden uns in Kürze bei Ihnen melden.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-sky-100 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-col items-center">
            <img 
              src="https://i.imgur.com/58Ph8zB.png" 
              alt="Armin Breig KFZ Logo" 
              className="h-32 w-auto mb-8 animate-fade-in"
            />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B4965] mb-6">
              KFZ - Sachverständigenbüro
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-2xl">
              Erfahrung schafft Vertrauen - Ihr kompetenter Partner für alle KFZ-Gutachten
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                element?.scrollIntoView({ behavior: 'smooth' });
              }} 
              className="btn-primary text-lg"
            >
              Jetzt Kontakt aufnehmen
            </button>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center">Unsere Services</h2>
          <p className="section-subtitle text-center">
            Als erfahrenes und zuverlässiges Sachverständigenbüro biete ich Ihnen umfassende
            Dienstleistungen rund um Ihr Fahrzeug.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <ServiceCard
              image="https://images.unsplash.com/photo-1613042964418-89c800809319?q=80&w=2984&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              title="Unfallgutachten"
              description="Professionelle und unabhängige Begutachtung von Unfallschäden für eine faire Schadensregulierung"
              icon={<ClipboardList className="h-6 w-6" />}
            />
            <ServiceCard
              image="https://images.unsplash.com/photo-1474039369477-5e74ff1f0e57?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              title="Oldtimerwertgutachten"
              description="Fachkundige Bewertung und Dokumentation klassischer Fahrzeuge"
              icon={<Timer className="h-6 w-6" />}
            />
            <ServiceCard
              image="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80"
              title="Kostenvoranschläge"
              description="Präzise Kostenermittlung für Reparaturen und Instandsetzungen"
              icon={<Car className="h-6 w-6" />}
            />
            <ServiceCard
              image="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=2583&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              title="Fahrzeugbewertungen"
              description="Neutrale und marktgerechte Wertermittlung für alle Fahrzeugarten"
              icon={<ClipboardList className="h-6 w-6" />}
            />
            <ServiceCard
              image="https://cdn.pixabay.com/photo/2020/05/24/08/16/charging-station-5212924_1280.jpg"
              title="Hybrid- und Elektrofahrzeuge"
              description="Spezialisierte Begutachtung von Hybrid- und Elektrofahrzeugen mit HV-Berechtigung"
              icon={<Zap className="h-6 w-6" />}
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center">Über Mich</h2>
          <p className="section-subtitle text-center">
            Sie suchen einen unabhängigen und zuverlässigen Kfz-Sachverständigen?<br />
            <span className="font-semibold">Somit endet Ihre Suche genau hier!!!</span>
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-12">
            {/* Text Content */}
            <div className="space-y-6 prose prose-lg">
              <p>
                Schon in meiner Jugend entfachte ich meine Leidenschaft und entwickelte eine starke Affinität 
                für Fahrzeuge aller Art und ihre Technik.
              </p>
              
              <p>
                Bereits sehr früh legte ich den Grundstein durch meine Ausbildung zum Kfz-Mechaniker für mein 
                Verständnis im Bereich Fahrzeugtechnik.
              </p>
              
              <p>
                Direkt im Anschluss absolvierte ich die Meisterprüfung im Kfz-Handwerk, wodurch ich 
                nicht nur meine handwerklichen Kenntnisse vertiefte, sondern auch über Jahre hinweg mein 
                Wissen und meine Erfahrungen in der Unfall- und Schadensabwicklung bei Premiummarken wie 
                Mercedes und BMW erweitern konnte.
              </p>
              
              <p>
                Durch meine Ausbildung zum Kfz-Sachverständigen sowie einer Ausbildung zur Elektrofachkraft 
                bin ich befähigt, Hochvolt-(HV-)Fahrzeuge spannungsfrei zu schalten und kann somit 
                Unfallschäden an einem Elektroauto begutachten und dessen elektrische Komponenten untersuchen.
              </p>
              
              <p>
                Als Kfz-Sachverständiger stehe ich Ihnen gerne zur Verfügung, um Ihrem Anliegen mit Herzblut 
                und Leidenschaft zu begegnen.
              </p>
            </div>

            {/* Image */}
            <div className="relative h-[600px] rounded-xl overflow-hidden shadow-xl lg:translate-x-12 transition-all duration-300 hover:translate-y-[-8px] hover:shadow-2xl">
              <img
                src="https://i.imgur.com/yJ6FUnM.jpeg"
                alt="Armin Breig KFZ-Sachverständiger"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Armin Breig</h3>
                <p className="text-white/90">Kompetenz und Service</p>
              </div>
            </div>
          </div>

          {/* DESAG Credentials */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-16">
            <div className="w-48 h-48 rounded-xl overflow-hidden shadow-lg bg-white p-2 flex items-center justify-center">
              <img
                src="/desag-abzeichen.jpg"
                alt="DESAG Verifiziertes Mitglied - Armin Breig Sachverständiger"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="w-48 h-48 rounded-xl overflow-hidden shadow-lg bg-white p-4 flex items-center justify-center">
              <img
                src="/desag-stempel.jpg"
                alt="DESAG Sachverständiger Stempel - Armin Breig Mitgl.-Nr. K/251201"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Benefits List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            <div className="flex items-start space-x-4">
              <Shield className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Zertifiziert und geprüft</h3>
                <p className="text-gray-600">KFZ-Meister / DESAG-geprüfter Sachverständiger für Schäden und Wertermittlung an Kraft- und Elektro-Hochvoltfahrzeugen.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Package className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Rundum-sorglospaket</h3>
                <p className="text-gray-600">Ich kümmere mich um alle Aspekte Ihres Anliegens</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Clock className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Gutachten in 24 Stunden</h3>
                <p className="text-gray-600">Schnelle und professionelle Bearbeitung Ihres Auftrags</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <CheckCircle2 className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Kostenlose Erstberatung</h3>
                <p className="text-gray-600">Unverbindliches Gespräch zur Klärung Ihrer Fragen</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <PhoneCall className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">24/7 für Sie erreichbar</h3>
                <p className="text-gray-600">Rund um die Uhr Unterstützung in Notfällen</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Scale className="w-8 h-8 text-[#FF6B35] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Unabhängig & Neutral</h3>
                <p className="text-gray-600">Objektive Bewertung ohne Interessenkonflikte</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center">Kontakt</h2>
          <p className="section-subtitle text-center">
            Ich bin für Sie da und freue mich auf Ihre Nachricht.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="space-y-6">
                <ContactCard
                  icon={<Phone className="h-6 w-6" />}
                  title="Telefon"
                  content="+49 172 9507571"
                />
                <ContactCard
                  icon={<Mail className="h-6 w-6" />}
                  title="E-Mail"
                  content="armin.breig@web.de"
                />
                <ContactCard
                  icon={<MapPin className="h-6 w-6" />}
                  title="Adresse"
                  content="Rosenstraße 5, 76356 Weingarten"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true"
                onSubmit={handleSubmit} 
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                    required
                  />
                </div>

                {formStatus.message && (
                  <div className={`p-4 rounded-md ${
                    formStatus.isSuccess ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus.isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus.isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;